/**
 * Certifications Module
 * Browse / upload / manage supplier-product certification PDFs.
 * Grouped view: Supplier → Model → Certificates
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, IconButton, Chip, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
  Accordion, AccordionSummary, AccordionDetails, Tooltip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  InputAdornment, Snackbar, Alert,
} from '@mui/material';
import {
  ExpandMore, CloudUpload, PictureAsPdf, Delete, Edit, Search,
  Verified, Add, Visibility, Download, ViewList, AccountTree,
} from '@mui/icons-material';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import api from '../../services/api';

const CERT_TYPES = ['FCC', 'CE', 'ROHS', 'BIS', 'RED', 'WPC', 'IEC', 'ISO', 'Other'];

function UploadDialog({ open, onClose, onSaved, edit }) {
  const [form, setForm] = useState({
    supplier: '', model_number: '', product: '', certificate_type: '',
    certificate_number: '', issue_date: '', expiry_date: '', notes: '',
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsedNote, setParsedNote] = useState('');

  useEffect(() => {
    if (open) {
      if (edit) {
        setForm({
          supplier:           edit.supplier           || '',
          model_number:       edit.model_number       || '',
          product:            edit.product            || '',
          certificate_type:   edit.certificate_type   || '',
          certificate_number: edit.certificate_number || '',
          issue_date:         edit.issue_date ? edit.issue_date.substring(0, 10) : '',
          expiry_date:        edit.expiry_date ? edit.expiry_date.substring(0, 10) : '',
          notes:              edit.notes              || '',
        });
      } else {
        setForm({ supplier: '', model_number: '', product: '', certificate_type: '',
                  certificate_number: '', issue_date: '', expiry_date: '', notes: '' });
      }
      setFile(null);
      setErr('');
    }
  }, [open, edit]);

  const handleChange = (k) => (e) => setForm(s => ({ ...s, [k]: e.target.value }));

  // When a PDF is chosen, send it to backend /extract and pre-fill empty fields.
  const handleFileChange = async (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setParsedNote('');
    if (!f) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await api.post('/certifications/extract', fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      const fields = data.fields || {};
      // Only fill currently-empty fields — never overwrite what the user typed.
      setForm(s => {
        const next = { ...s };
        let filled = 0;
        Object.entries(fields).forEach(([k, v]) => {
          if (v && !next[k]) { next[k] = v; filled++; }
        });
        setParsedNote(filled ? `Auto-filled ${filled} field${filled > 1 ? 's' : ''} from PDF` : 'Could not auto-detect fields — please fill manually');
        return next;
      });
    } catch (err) {
      setParsedNote('PDF parse failed; fill manually');
    }
    setParsing(false);
  };

  const submit = async () => {
    if (!form.supplier.trim()) return setErr('Supplier is required');
    if (!edit && !file)        return setErr('Please choose a PDF file');

    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      if (file) fd.append('file', file);

      if (edit) {
        await api.put(`/certifications/${edit.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post('/certifications', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{edit ? 'Edit Certification' : 'Upload Certification'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Supplier *" value={form.supplier} onChange={handleChange('supplier')} fullWidth size="small" />
          <Stack direction="row" spacing={2}>
            <TextField label="Model Number" value={form.model_number} onChange={handleChange('model_number')} fullWidth size="small" />
            <TextField label="Product" value={form.product} onChange={handleChange('product')} fullWidth size="small" />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField select label="Certificate Type" value={form.certificate_type} onChange={handleChange('certificate_type')} fullWidth size="small">
              <MenuItem value="">—</MenuItem>
              {CERT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField label="Certificate Number" value={form.certificate_number} onChange={handleChange('certificate_number')} fullWidth size="small" />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Issue Date" type="date" InputLabelProps={{ shrink: true }} value={form.issue_date} onChange={handleChange('issue_date')} fullWidth size="small" />
            <TextField label="Expiry Date" type="date" InputLabelProps={{ shrink: true }} value={form.expiry_date} onChange={handleChange('expiry_date')} fullWidth size="small" />
          </Stack>
          <TextField label="Notes" value={form.notes} onChange={handleChange('notes')} fullWidth multiline rows={2} size="small" />

          <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1, textAlign: 'center' }}>
            <input
              id="cert-file"
              type="file"
              accept="application/pdf"
              hidden
              onChange={handleFileChange}
            />
            <label htmlFor="cert-file">
              <Button component="span" startIcon={<CloudUpload />} variant="outlined" size="small">
                {file ? 'Change PDF' : (edit ? 'Replace PDF (optional)' : 'Choose PDF *')}
              </Button>
            </label>
            {file && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                <PictureAsPdf sx={{ fontSize: 14, verticalAlign: 'middle', color: 'error.main' }} /> {file.name}
              </Typography>
            )}
            {parsing && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">Reading PDF…</Typography>
              </Box>
            )}
            {parsedNote && !parsing && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: parsedNote.startsWith('Auto-filled') ? 'success.main' : 'text.secondary' }}>
                {parsedNote}
              </Typography>
            )}
            {edit && !file && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                Current: {edit.original_filename}
              </Typography>
            )}
          </Box>

          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy} startIcon={busy ? <CircularProgress size={14} /> : <Add />}>
          {edit ? 'Save' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Certifications() {
  const [tree,    setTree]    = useState({});
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [search,  setSearch]  = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const [viewMode,   setViewMode]   = useState(localStorage.getItem('certs_view') || 'table');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/certifications/grouped');
      setTree(r.data.data || {});
      setTotal(r.data.total || 0);
    } catch (e) {
      setSnack({ open: true, msg: e.response?.data?.message || e.message, sev: 'error' });
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this certification?')) return;
    try {
      await api.delete(`/certifications/${id}`);
      setSnack({ open: true, msg: 'Deleted', sev: 'success' });
      fetchData();
    } catch (e) {
      setSnack({ open: true, msg: e.response?.data?.message || e.message, sev: 'error' });
    }
  };

  const fileUrl = (id) => {
    const base = process.env.NODE_ENV === 'production' ? '/IdentiumFx/api' : '/api';
    const token = localStorage.getItem('fx_token');
    return `${base}/certifications/${id}/file?_t=${token ? 1 : 0}`;
  };

  // Open PDF in new tab with auth header by fetching as blob
  const openPdf = async (id, filename) => {
    try {
      const r = await api.get(`/certifications/${id}/file`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) {
      setSnack({ open: true, msg: 'Failed to open PDF', sev: 'error' });
    }
  };

  const downloadPdf = async (id, filename) => {
    try {
      const r = await api.get(`/certifications/${id}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `certification_${id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setSnack({ open: true, msg: 'Failed to download', sev: 'error' });
    }
  };

  // Filter tree by search query (client-side)
  const filteredTree = (() => {
    if (!search.trim()) return tree;
    const q = search.toLowerCase();
    const out = {};
    Object.entries(tree).forEach(([sup, models]) => {
      const filteredModels = {};
      Object.entries(models).forEach(([mod, certs]) => {
        const matched = certs.filter(c =>
          (sup || '').toLowerCase().includes(q) ||
          (mod || '').toLowerCase().includes(q) ||
          (c.product || '').toLowerCase().includes(q) ||
          (c.certificate_number || '').toLowerCase().includes(q) ||
          (c.original_filename || '').toLowerCase().includes(q)
        );
        if (matched.length) filteredModels[mod] = matched;
      });
      if (Object.keys(filteredModels).length) out[sup] = filteredModels;
    });
    return out;
  })();

  const supplierKeys = Object.keys(filteredTree).sort();

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Verified color="primary" /> Certifications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Supplier and product certification documents — {total} total
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <ToggleButtonGroup size="small" exclusive value={viewMode}
            onChange={(_, v) => { if (v) { setViewMode(v); localStorage.setItem('certs_view', v); } }}>
            <ToggleButton value="table"><ViewList fontSize="small" sx={{ mr: 0.5 }} /> Table</ToggleButton>
            <ToggleButton value="grouped"><AccountTree fontSize="small" sx={{ mr: 0.5 }} /> Grouped</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            placeholder="Search supplier, model, product…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 280 }}
          />
          <Button variant="contained" startIcon={<CloudUpload />} onClick={() => { setEditing(null); setOpenDialog(true); }}>
            Upload Certificate
          </Button>
        </Stack>
      </Box>

      {/* Body */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : supplierKeys.length === 0 ? (
        <Paper elevation={1} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <Verified sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
          <Typography color="text.secondary">No certifications yet. Click "Upload Certificate" to begin.</Typography>
        </Paper>
      ) : viewMode === 'table' ? (
        // ── FLAT TABLE VIEW ────────────────────────────────────────────────
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Cert / Reg #</TableCell>
                <TableCell>Issue</TableCell>
                <TableCell>Expiry</TableCell>
                <TableCell>File</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                // Flatten the tree into a single sorted list
                const all = [];
                supplierKeys.forEach(sup => {
                  Object.entries(filteredTree[sup]).forEach(([mod, certs]) => {
                    certs.forEach(c => all.push({ ...c, supplier: sup, model_number: c.model_number || mod }));
                  });
                });
                all.sort((a, b) => (a.supplier || '').localeCompare(b.supplier || '') ||
                  (a.model_number || '').localeCompare(b.model_number || ''));
                return all.map((c, i) => {
                  const isExpired = c.expiry_date && new Date(c.expiry_date) < new Date();
                  return (
                    <TableRow key={c.id} hover sx={{ bgcolor: isExpired ? 'error.50' : undefined }}>
                      <TableCell sx={{ color: 'text.secondary', fontSize: 11 }}>{i + 1}</TableCell>
                      <TableCell><Typography variant="body2" fontWeight={600}>{c.supplier}</Typography></TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{c.model_number || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{c.product || '—'}</TableCell>
                      <TableCell>{c.certificate_type ? <Chip label={c.certificate_type} size="small" color="success" variant="outlined" /> : '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{c.certificate_number || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{c.issue_date ? c.issue_date.substring(0,10) : '—'}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: isExpired ? 'error.main' : undefined, fontWeight: isExpired ? 700 : undefined }}>
                        {c.expiry_date ? c.expiry_date.substring(0,10) : '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={c.original_filename || ''}>
                          <Box component="span">
                            <PictureAsPdf sx={{ fontSize: 14, color: 'error.main', verticalAlign: 'middle', mr: 0.5 }} />
                            {c.original_filename || '—'}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View"><IconButton size="small" onClick={() => openPdf(c.id, c.original_filename)}><Visibility fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Download"><IconButton size="small" onClick={() => downloadPdf(c.id, c.original_filename)}><Download fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(c); setOpenDialog(true); }}><Edit fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(c.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                });
              })()}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        supplierKeys.map(sup => {
          const models = filteredTree[sup];
          const totalCerts = Object.values(models).reduce((s, arr) => s + arr.length, 0);
          return (
            <Accordion key={sup} defaultExpanded sx={{ mb: 1, borderRadius: 1, '&:before': { display: 'none' } }} elevation={1}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: '#f5f7fb', borderRadius: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                  <Typography fontWeight={700}>{sup}</Typography>
                  <Chip label={`${totalCerts} cert${totalCerts !== 1 ? 's' : ''}`} size="small" color="primary" variant="outlined" />
                  <Chip label={`${Object.keys(models).length} model${Object.keys(models).length !== 1 ? 's' : ''}`} size="small" variant="outlined" />
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1 }}>
                {Object.entries(models).sort(([a], [b]) => a.localeCompare(b)).map(([mod, certs]) => (
                  <Box key={mod} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ pl: 1, color: 'primary.main', fontWeight: 700, mb: 0.5 }}>
                      {mod} <Typography component="span" variant="caption" color="text.secondary">({certs.length})</Typography>
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50', fontSize: 12 } }}>
                            <TableCell>Product</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Cert #</TableCell>
                            <TableCell>Issue</TableCell>
                            <TableCell>Expiry</TableCell>
                            <TableCell>Filename</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {certs.map(c => (
                            <TableRow key={c.id} hover>
                              <TableCell>{c.product || '—'}</TableCell>
                              <TableCell>{c.certificate_type ? <Chip label={c.certificate_type} size="small" color="success" variant="outlined" /> : '—'}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{c.certificate_number || '—'}</TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{c.issue_date ? c.issue_date.substring(0,10) : '—'}</TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{c.expiry_date ? c.expiry_date.substring(0,10) : '—'}</TableCell>
                              <TableCell sx={{ fontSize: 12, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <Tooltip title={c.original_filename || ''}>
                                  <Box component="span">
                                    <PictureAsPdf sx={{ fontSize: 14, color: 'error.main', verticalAlign: 'middle', mr: 0.5 }} />
                                    {c.original_filename || '—'}
                                  </Box>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="View"><IconButton size="small" onClick={() => openPdf(c.id, c.original_filename)}><Visibility fontSize="small" /></IconButton></Tooltip>
                                <Tooltip title="Download"><IconButton size="small" onClick={() => downloadPdf(c.id, c.original_filename)}><Download fontSize="small" /></IconButton></Tooltip>
                                <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(c); setOpenDialog(true); }}><Edit fontSize="small" /></IconButton></Tooltip>
                                <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(c.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          );
        })
      )}

      <UploadDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSaved={() => { fetchData(); setSnack({ open: true, msg: editing ? 'Updated' : 'Uploaded', sev: 'success' }); }}
        edit={editing}
      />
      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
